import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient, formatApiError, setOnAuthFailure, markSession, clearSession } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
      markSession();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Best-effort: clear any pre-June-2026 token still in localStorage.
    try { localStorage.removeItem("ntn_token"); } catch (_e) { /* noop */ }
    setOnAuthFailure(() => setUser(false));
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      // Auth is cookie-based. No token is persisted to localStorage.
      setUser(data);
      markSession();
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await apiClient.post("/auth/register", payload);
      setUser(data);
      markSession();
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await apiClient.post("/auth/logout"); } catch (_e) { /* noop */ }
    try { localStorage.setItem("ntn_forced_logout", String(Date.now()) + ":manual"); } catch (_e) { /* noop */ }
    try { localStorage.removeItem("ntn_token"); } catch (_e) { /* noop */ }
    clearSession();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, refresh: fetchMe,
      hasPermission: (perm) => Boolean(user && user.effective_permissions?.includes(perm)),
      reportScope: user?.report_scope || "self",
      roleName: user?.role_name || null,
      // Phase E.4: canonical display label with server-side fallback chain
      // (display_name → legal_name → name → legacy user.company snapshot).
      // NTN staff and applicants deliberately get null so their surfaces
      // are never mis-branded as the client's company.
      companyLabel: user?.role === "property_manager"
        ? (user?.company_display_label || null)
        : null,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
